import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { buildNflAllTimeGreatsPath } from "../../constants/nflAllTimeGreats";
import { serializeHeadshotIds } from "../../utils/playerHeadshots";
import { US_MAJOR_SCOPE } from "../../utils/nflLeagueScope";
import { buildNflSimilarSeasonsPath } from "../../components/similarSeasons/similarSeasonsQuery";

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8008/api";
  }
  return "/api";
};

const mapBioData = (player) => {
  if (!player) return player;
  if (player.bioData) {
    return { ...player, bio: player.bioData };
  }
  return player;
};

const mapPlayers = (players = []) => players.map(mapBioData);

export const nflApi = createApi({
  reducerPath: "nflApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      headers.set("Cache-Control", "no-cache");

      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
    responseHandler: async (response) => {
      const data = await response.json();

      if (response.status === 403 && data.code === "ADMIN_REQUIRED") {
        window.location.href = "/app";
      }

      return data;
    },
  }),
  tagTypes: ["NflPlayer", "NflLeaderboard"],
  endpoints: (builder) => ({
    getNflPlayerById: builder.query({
      query: (id) => `/nfl/players/${id}`,
      transformResponse: mapBioData,
      providesTags: (result, error, id) => [{ type: "NflPlayer", id }],
    }),

    getNflSimilarSeasons: builder.query({
      query: ({
        playerId,
        season,
        role,
        limit = 8,
        excludeSamePlayer = true,
      } = {}) =>
        buildNflSimilarSeasonsPath({
          playerId,
          season,
          role,
          limit,
          excludeSamePlayer,
        }),
    }),

    searchNflPlayers: builder.query({
      query: (searchTerm) =>
        `/nfl/players/search?name=${encodeURIComponent(searchTerm)}`,
      transformResponse: (response) => mapPlayers(response.results || []),
      providesTags: ["NflPlayer"],
    }),

    getNflPositionLeaders: builder.query({
      query: (slot) =>
        `/nfl/players/position-leaders/${encodeURIComponent(slot)}`,
      providesTags: (_result, _error, slot) => [
        { type: "NflPlayer", id: `position-leaders-${slot}` },
      ],
    }),

    autocompleteNflPlayers: builder.query({
      query: (q) =>
        `/nfl/players/autocomplete?q=${encodeURIComponent(q)}`,
      transformResponse: (response) => mapPlayers(response),
      providesTags: ["NflPlayer"],
    }),

    getNflTopPlayers: builder.query({
      query: ({ category = "passing-yards", leagueScope } = {}) => {
        const params = new URLSearchParams();
        if (leagueScope && leagueScope !== US_MAJOR_SCOPE) {
          params.set("leagueScope", leagueScope);
        }
        const qs = params.toString();
        return `/nfl/top-players/${category}${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (response) => ({
        ...response,
        players: mapPlayers(response.players || []),
      }),
      providesTags: ["NflLeaderboard"],
    }),

    getNflAllTimeGreatsLeaders: builder.query({
      query: ({ stat, page = 1, pageSize = 25, leagueScope } = {}) =>
        buildNflAllTimeGreatsPath({ stat, page, pageSize, leagueScope }),
      providesTags: ["NflLeaderboard"],
    }),

    getNflPlayerHeadshots: builder.query({
      query: (ids) =>
        `/nfl/players/headshots?ids=${serializeHeadshotIds(ids)}`,
      transformResponse: (response) => response.headshots || {},
      keepUnusedDataFor: 86400,
    }),

    getNflTeamPlayers: builder.query({
      query: (franchiseId) => `/nfl/teams/${encodeURIComponent(franchiseId)}`,
      providesTags: (result, error, franchiseId) => [
        { type: "NflPlayer", id: `team-${franchiseId}` },
      ],
    }),
  }),
});

export const {
  useGetNflPlayerByIdQuery,
  useSearchNflPlayersQuery,
  useGetNflPositionLeadersQuery,
  useAutocompleteNflPlayersQuery,
  useGetNflTopPlayersQuery,
  useGetNflAllTimeGreatsLeadersQuery,
  useGetNflPlayerHeadshotsQuery,
  useGetNflTeamPlayersQuery,
  useGetNflSimilarSeasonsQuery,
} = nflApi;
